import moment from 'moment';
import toast from 'react-hot-toast';
import { CheckCircleIcon, InfoIcon, HandsClappingIcon, NotePencilIcon, CopyIcon } from '@phosphor-icons/react';
import { Avatar, AvatarImage, AvatarFallback } from '../../../components/ui/avatar';
import SolutionImage from './SolutionImage';
import { Button } from '../../../components/ui/button';
import {
  COMMUNITY_SOLUTION_SUPPORT_EMAIL,
  buildCommunitySolutionSupportMailto,
} from '../../../utils/communitySolutionSupportMail';

const handleCopyId = (id) => {
  navigator.clipboard.writeText(id);
  toast.success('Solution id copied');
};

const SolutionIdLine = ({ solutionId, className }) => {
  if (!solutionId) return null;

  return (
    <p className={className}>
      Use this solution id to contact us: <span className="font-semibold">{solutionId}</span>{' '}
      <button
        type="button"
        onClick={() => handleCopyId(solutionId)}
        className="cursor-pointer inline-flex items-center gap-1 underline text-[var(--q3-blue-normal)]"
      >
        copy id
        <CopyIcon size={12} />
      </button>
    </p>
  );
};

const StatusBanner = ({ solutionId, userEmail, status, rejection, hasWriteAccess, canEdit, blockReason, onEdit }) => {
  const supportMailto = buildCommunitySolutionSupportMailto(userEmail, solutionId);

  if (status === 'pending') {
    return (
      <div className="border border-dashed border-[var(--q3-yellow-normal)] bg-[var(--q3-yellow-light)] rounded-lg px-4 py-3">
        <div className="flex items-center gap-2 text-[var(--q3-yellow-normal)] text-sm">
          Your solution is getting reviewed
        </div>
        <p className="text-sm text-[var(--q3-yellow-dark)] mt-1">
          We will notify you about status of your solution once it&apos;s reviewed
        </p>
      </div>
    );
  }

  if (status === 'approved') {
    return (
      <div className="border border-dashed border-[var(--q3-success-normal)] bg-[var(--q3-success-light)] rounded-lg px-4 py-3">
        <div className="flex items-center gap-2 text-[var(--q3-success-normal)] text-xs md:text-sm font-bold">
          <CheckCircleIcon size={18} />
          Published
        </div>
      </div>
    );
  }

  if (status === 'rejected') {
    return (
      <div className="border border-dashed border-[var(--q3-danger-normal)] bg-[var(--q3-danger-light)] rounded-lg px-4 py-3 flex flex-col md:flex-row items-stretch md:items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[var(--q3-danger-normal)] font-semibold text-sm">
            <InfoIcon size={18} />
            Solution rejected
          </div>
          <p className="text-xs md:text-sm text-[var(--q3-neutral-default)] mt-1">Your solution has been rejected.</p>
          {rejection?.reason && (
            <p className="text-xs md:text-sm text-[var(--q3-neutral-default)]">Rejection Reason: {rejection.reason}</p>
          )}
          <p className="text-xs md:text-sm text-[var(--q3-neutral-default)] mt-1">
            If you think this is a mistake you can contact us on{' '}
            <a href={supportMailto} className="text-[var(--q3-blue-normal)]">
              {COMMUNITY_SOLUTION_SUPPORT_EMAIL}
            </a>
          </p>
          {!hasWriteAccess && (
            <p className="text-xs md:text-sm text-[var(--q3-neutral-default)] mt-1">
              You have been blocked{blockReason ? `, as ${blockReason}` : ' from submitting/editing solutions'}.
            </p>
          )}
          <SolutionIdLine
            solutionId={solutionId}
            className="text-xs md:text-sm text-[var(--q3-neutral-default)] mt-2"
          />
        </div>
        {canEdit && (
          <Button
            type="button"
            onClick={() => onEdit('edit')}
            className="flex items-center justify-center gap-1 font-medium px-4 py-2 rounded-full whitespace-nowrap hover:scale-100 w-full md:w-auto"
          >
            Edit Solution
          </Button>
        )}
      </div>
    );
  }

  if (status === 'removed') {
    return (
      <div className="border border-dashed border-[var(--q3-danger-normal)] bg-[var(--q3-danger-light)] rounded-lg px-4 py-3 flex flex-col md:flex-row items-stretch md:items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[var(--q3-danger-normal)] text-sm">
            <InfoIcon size={18} />
            Solution removed
          </div>
          <p className="text-sm text-[var(--q3-danger-dark)] mt-1">Your solution has been removed.</p>
          {rejection?.reason && (
            <p className="text-sm text-[var(--q3-danger-dark)]">Rejection Reason: {rejection.reason}</p>
          )}
          <p className="text-sm text-[var(--q3-danger-dark)]">
            If you think this is a mistake you can contact us on{' '}
            <a href={supportMailto} className="text-[var(--q3-blue-normal)]">
              {COMMUNITY_SOLUTION_SUPPORT_EMAIL}
            </a>
          </p>
          {!hasWriteAccess && (
            <p className="text-sm text-[var(--q3-danger-dark)] mt-1">
              You have been blocked{blockReason ? `, as ${blockReason}` : ' from submitting/editing solutions'}.
            </p>
          )}
          <SolutionIdLine solutionId={solutionId} className="text-sm text-[var(--q3-danger-dark)] mt-2" />
        </div>
        {canEdit && (
          <Button
            type="button"
            onClick={() => onEdit('resubmit')}
            className="flex items-center justify-center gap-1 font-medium px-4 py-2 rounded-full whitespace-nowrap hover:scale-100 w-full md:w-auto"
          >
            Edit Solution
          </Button>
        )}
      </div>
    );
  }

  return null;
};

const MySolutionCard = ({ solution, user, hasWriteAccess, blockReason, maxEditCount, onEdit }) => {
  const editCount = solution.editCount ?? 0;
  const editLimitReached = typeof maxEditCount === 'number' && editCount >= maxEditCount;
  const canEdit = hasWriteAccess && !editLimitReached;

  return (
    <div className="border border-[var(--q3-stroke-light)] rounded-xl overflow-hidden max-w-[712px]">
      <div className="px-4 py-3 border-b border-[var(--q3-stroke-light)]">
        <span className="text-sm font-medium text-[var(--q3-neutral-light)]">SOLUTION BY YOU</span>
      </div>

      <div className="p-4 flex flex-col gap-3">
        <div className="flex flex-row items-center justify-between">
          <div className="flex flex-row items-center gap-3">
            <Avatar>
              <AvatarImage src={user?.profilePicture} alt={user?.name} />
              <AvatarFallback>{user?.name?.[0] || 'U'}</AvatarFallback>
            </Avatar>
            <div className="flex flex-col">
              <span className="text-sm font-semibold text-[var(--q3-neutral-default)]">{user?.name || 'You'}</span>
              <span className="text-xs text-[var(--q3-neutral-light)]">
                {moment(solution.createdAt).format("DD MMM 'YY")}
              </span>
            </div>
          </div>
          {solution.status === 'approved' && canEdit && (
            <Button
              onClick={() => onEdit('edit')}
              className="flex items-center gap-1 text-sm font-medium rounded-full bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-normal)] text-[var(--q3-neutral-default)] hover:scale-100 hover:bg-[var(--q3-surface-light)] duration-200 shadow-lg"
            >
              <NotePencilIcon size={16} className="text-[var(--q3-danger-normal)]" />
              Edit
            </Button>
          )}
        </div>

        <SolutionImage images={solution?.solution?.images} />

        {solution.status === 'approved' && (
          <div className="flex items-center gap-1 whitespace-nowrap text-xs md:text-sm text-[var(--q3-neutral-default)] w-fit border border-[var(--q3-stroke-light)] rounded-full px-3 py-1.5">
            <HandsClappingIcon className="text-sm md:text-base" />
            Appreciate • {solution.upvotes ?? 0}
          </div>
        )}

        <StatusBanner
          solutionId={solution._id}
          userEmail={user?.email}
          status={solution.status}
          rejection={solution.rejection}
          hasWriteAccess={hasWriteAccess}
          canEdit={canEdit}
          blockReason={blockReason}
          onEdit={onEdit}
        />
      </div>
    </div>
  );
};

export default MySolutionCard;
